/* eslint-disable @typescript-eslint/no-explicit-any */
import type {
    AccountInfo,
    IPublicClientApplication,
} from "@azure/msal-browser";
import { scopes } from "../authentication/PublicClientApplication";
import { API_BASE_URL } from "../constants";
import type { ArbeitszeitItem } from "../models/ArbeitszeitItem";
import type { ProjektItem } from "../models/ProjektItem";
import type { ProjektPartnerZuweisungItem } from "../models/ProjektPartnerZuweisungItem";
import type { Projektrolle } from "../models/ProjektRolle";
import type { RechnungItem } from "../models/RechnungItem";
import type { AuswertungBisProjektItem } from "../models/reports/AuswertungBisProjektItem";
import type { AuswertungItem } from "../models/reports/AuswertungItem";
import type { AuswertungParalleleProjekteItem } from "../models/reports/AuswertungParalleleProjekteItem";
import type { AuswertungProjektberichtItem } from "../models/reports/AuswertungProjektberichtItem";
import type { AuswertungProjekteInstituteItem } from "../models/reports/AuswertungProjekteInstituteItem";
import type { AuswertungProjektfortschrittItem } from "../models/reports/AuswertungProjektfortschrittItem";
import type { AuswertungProjektleiterItem } from "../models/reports/AuswertungProjektleiterItem";
import type { AuswertungProjektmitarbeiterIststunden } from "../models/reports/AuswertungProjektmitarbeiterIststunden";
import type { AuswertungProjektmitarbeiterItem } from "../models/reports/AuswertungProjektmitarbeiterItem";
import type { AuswertungProjektmitarbeiterSammelItem } from "../models/reports/AuswertungProjektmitarbeiterSammelItem";
import type { AuswertungProjektpartnerItem } from "../models/reports/AuswertungProjektpartnerItem";
import type { AuswertungRechnungenItem } from "../models/reports/AuswertungRechnungenItem";
import type { AuswertungRessourcenItem } from "../models/reports/AuswertungRessourcenItem";
import type { AuswertungStundenreduktionItem } from "../models/reports/AuswertungStundenreduktionItem";
import type { AuswertungZahlungsplanItem } from "../models/reports/AuswertungZahlungsplanItem";
import type { ReportItem } from "../models/reports/ReportItem";

const REPORTS_BASE_URL = `${API_BASE_URL}/fhelix/imcprojectsreports`;

type QueryValue = string | number | boolean | Date | null | undefined;

function toQueryString(params: Record<string, QueryValue>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    searchParams.append(
      key,
      value instanceof Date ? value.toISOString() : String(value),
    );
  }
  const query = searchParams.toString();
  return query ? `?${query}` : "";
}

export class ReportService {
  private instance: IPublicClientApplication;
  private account: AccountInfo;

  constructor(instance: IPublicClientApplication, account: AccountInfo) {
    this.instance = instance;
    this.account = account;
  }

  async fetchWithAuth(url: string, options: RequestInit = {}) {
    let lastError: Error | null = null;
    const maxRetries = 2; // One retry for transient network errors

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await this.instance.acquireTokenSilent({
          scopes,
          account: this.account,
        });
        const headers = {
          ...options.headers,
          Authorization: `Bearer ${response.accessToken}`,
        };
        const fetchResult = await fetch(url, {
          ...options,
          headers,
          signal: options.signal,
        });

        // If successful, return immediately
        if (fetchResult.ok || fetchResult.status === 404) {
          return fetchResult;
        }

        // For certain errors, don't retry
        if (fetchResult.status === 401 || fetchResult.status === 403) {
          return fetchResult;
        }

        // For other errors, save and potentially retry
        lastError = new Error(
          `HTTP ${fetchResult.status}: ${fetchResult.statusText}`,
        );

        if (attempt < maxRetries - 1) {
          console.warn(`⚠️ Fetch attempt ${attempt + 1} failed, retrying...`);
          await new Promise((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1)),
          );
        }

        return fetchResult;
      } catch (error: any) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // If silent token acquisition fails, try interactive
        if (
          error?.errorCode === "interaction_required" ||
          error?.message?.includes("interaction_required") ||
          error?.message?.includes("AADSTS160021")
        ) {
          console.warn("Session expired. Attempting interactive login...");
          try {
            const response = await this.instance.acquireTokenPopup({
              scopes,
              account: this.account,
            });
            const headers = {
              ...options.headers,
              Authorization: `Bearer ${response.accessToken}`,
            };
            return fetch(url, { ...options, headers, signal: options.signal });
          } catch (interactiveError) {
            console.error(
              "Interactive token acquisition failed:",
              interactiveError,
            );
            // Dispatch event to notify the app that re-login is needed
            window.dispatchEvent(
              new CustomEvent("auth-required", {
                detail: {
                  message:
                    "Session expired. Please refresh the page and login again.",
                },
              }),
            );
            throw new Error(
              "Session expired. Please refresh the page and login again.",
            );
          }
        }

        // For network errors, try once more
        if (
          attempt < maxRetries - 1 &&
          (error?.message?.includes("Failed to fetch") ||
            error?.message?.includes("ERR_") ||
            error?.name === "TypeError")
        ) {
          console.warn(
            `⚠️ Network error on attempt ${attempt + 1}, retrying...`,
          );
          await new Promise((resolve) =>
            setTimeout(resolve, 500 * (attempt + 1)),
          );
          continue;
        }

        throw lastError;
      }
    }

    throw lastError || new Error("Fetch operation failed");
  }

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    const res = await this.send(url, options);
    return res.json() as Promise<T>;
  }

  // For endpoints that return raw text (e.g. XML), which is not JSON-encoded.
  private async requestText(url: string, options: RequestInit = {}): Promise<string> {
    const res = await this.send(url, options);
    return res.text();
  }

  private async send(url: string, options: RequestInit): Promise<Response> {
    const res = await this.fetchWithAuth(url, options);
    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error:", res.status, res.statusText, errorText);
      throw new Error(
        `Error loading report: ${res.status} ${res.statusText} - ${errorText}`,
      );
    }
    return res;
  }

  async getAdministrationListe(rolle: Projektrolle): Promise<ReportItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/administrationliste${toQueryString({ rolle })}`,
      { method: "GET" },
    );
  }

  async getReportListe(rolle: Projektrolle): Promise<ReportItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/reportliste${toQueryString({ rolle })}`,
      { method: "GET" },
    );
  }

  async getAuswertungZuStellendeRechnungen(): Promise<
    AuswertungZahlungsplanItem[]
  > {
    return this.request(`${REPORTS_BASE_URL}/auswertungzustellenderechnungen`, {
      method: "GET",
    });
  }

  async getAuswertungOffeneRechnungen(): Promise<AuswertungRechnungenItem[]> {
    return this.request(`${REPORTS_BASE_URL}/auswertungoffenerechnungen`, {
      method: "GET",
    });
  }

  async getAuswertungMitarbeiterProjekte(
    userId: number,
  ): Promise<AuswertungProjektmitarbeiterItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungmitarbeiterprojekte${toQueryString({ userId })}`,
      { method: "GET" },
    );
  }

  async getAuswertungAlleMitarbeiterProjekte(): Promise<
    AuswertungProjektmitarbeiterItem[]
  > {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungallemitarbeiterprojekte`,
      { method: "GET" },
    );
  }

  async getAuswertungAlleMitarbeiterProjekteSammel(): Promise<
    AuswertungProjektmitarbeiterSammelItem[]
  > {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungallemitarbeiterprojektesammel`,
      { method: "GET" },
    );
  }

  async getAuswertungProjektpartnerProjekte(): Promise<
    AuswertungProjektpartnerItem[]
  > {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungprojektpartnerprojekte`,
      { method: "GET" },
    );
  }

  async getAuswertungProjekteProjektpartner(): Promise<
    AuswertungProjektpartnerItem[]
  > {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungprojekteprojektpartner`,
      { method: "GET" },
    );
  }

  async getAuswertungFortschrittsberichteProjekte(): Promise<
    AuswertungProjektfortschrittItem[]
  > {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungfortschrittsberichteprojekte`,
      { method: "GET" },
    );
  }

  async getAuswertungBerichteProjekte(): Promise<
    AuswertungProjektberichtItem[]
  > {
    return this.request(`${REPORTS_BASE_URL}/auswertungberichteprojekte`, {
      method: "GET",
    });
  }

  async getAuswertungBisProjekte(
    meldeDatum: Date,
  ): Promise<AuswertungBisProjektItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungbisprojekte${toQueryString({ meldeDatum })}`,
      { method: "GET" },
    );
  }

  async getAuswertungBisProjekteAll(
    meldeDatum: Date,
  ): Promise<AuswertungBisProjektItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungbisprojekteall${toQueryString({ meldeDatum })}`,
      { method: "GET" },
    );
  }

  async getReportString(reportType: string): Promise<string> {
    return this.request(
      `${REPORTS_BASE_URL}/getreportstring${toQueryString({ reportType })}`,
      { method: "GET" },
    );
  }

  async getBisMeldungString(meldeDatum: Date): Promise<string> {
    // Local date parts are used, because toISOString() would shift a local midnight to the previous day in UTC.
    const pad = (n: number) => String(n).padStart(2, "0");
    const dateOnly = `${meldeDatum.getFullYear()}-${pad(meldeDatum.getMonth() + 1)}-${pad(meldeDatum.getDate())}`;
    return this.requestText(
      `${REPORTS_BASE_URL}/getbismeldungstring${toQueryString({ meldeDatum: dateOnly })}`,
      { method: "GET" },
    );
  }

  async getAuswertungBisProjekteVonBis(
    meldeZeitraumVon: Date,
    meldeZeitraumBis: Date,
  ): Promise<ProjektItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungbisprojektevonbis${toQueryString({ meldeZeitraumVon, meldeZeitraumBis })}`,
      { method: "GET" },
    );
  }

  async getAuswertungAktiveProjekte(): Promise<AuswertungItem[]> {
    return this.request(`${REPORTS_BASE_URL}/auswertungaktiveprojekte`, {
      method: "GET",
    });
  }

  async getAuswertungProjektstatus(): Promise<AuswertungItem[]> {
    return this.request(`${REPORTS_BASE_URL}/auswertungprojektstatus`, {
      method: "GET",
    });
  }

  async getAuswertungAktivePublikationen(
    dateStart: Date,
    dateEnde: Date,
  ): Promise<AuswertungItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungaktivepublikationen${toQueryString({ dateStart, dateEnde })}`,
      { method: "GET" },
    );
  }

  async getAuswertungIststundenZeitaufzeichnung(
    userid: number,
    start?: Date | null,
    end?: Date | null,
  ): Promise<AuswertungProjektmitarbeiterIststunden[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungistzeitaufzeichnung${toQueryString({ userid, start, end })}`,
      { method: "GET" },
    );
  }

  async getAuswertungProjektmitarbeiterArbeitszeiten(
    userid: number,
    start?: Date | null,
    end?: Date | null,
  ): Promise<ArbeitszeitItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungprojektmitarbeiterarbeitszeiten${toQueryString({ userid, start, end })}`,
      { method: "GET" },
    );
  }

  async getAuswertungRessourcenplanung(): Promise<AuswertungRessourcenItem[]> {
    return this.request(`${REPORTS_BASE_URL}/auswertungressourcenplanung`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async getAuswertungRessourcenplanunguser(): Promise<
    AuswertungRessourcenItem[]
  > {
    return this.request(`${REPORTS_BASE_URL}/auswertungressourcenplanunguser`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  async getAuswertungParalleleProjekte(
    start: Date,
    end: Date,
  ): Promise<AuswertungParalleleProjekteItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungparalleleprojekte${toQueryString({ start, end })}`,
      { method: "GET" },
    );
  }

  async getAuswertungProjektleiterProjekte(): Promise<
    AuswertungProjektleiterItem[]
  > {
    return this.request(`${REPORTS_BASE_URL}/auswertungprojektleiterprojekte`, {
      method: "GET",
    });
  }

  async getAuswertungStundenreduktionItems(): Promise<
    AuswertungStundenreduktionItem[]
  > {
    return this.request(`${REPORTS_BASE_URL}/auswertungstundenreduktion`, {
      method: "GET",
    });
  }

  async getAuswertungStundenreduktionItemsFHelixVergleich(
    onlyActualAndRecentSemesters: boolean,
  ): Promise<AuswertungStundenreduktionItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungstundenreduktionfhelixvergleich${toQueryString({ onlyActualAndRecentSemesters })}`,
      { method: "GET" },
    );
  }

  async getAuswertungProjektmitarbeiterArbeitszeitenVergleich(): Promise<
    AuswertungStundenreduktionItem[]
  > {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungprojektmitarbeiterarbeitszeitenvergleich`,
      { method: "GET" },
    );
  }

  async getAuswertungProjektePerInstitute(
    von?: Date | null,
    bis?: Date | null,
  ): Promise<AuswertungProjekteInstituteItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungprojekteperinstitute${toQueryString({ von, bis })}`,
      { method: "GET" },
    );
  }

  async getRechnungenListeBIS(projektIDs: number[]): Promise<RechnungItem[]> {
    return this.request(`${REPORTS_BASE_URL}/rechnungenlistebis`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projektIDs),
    });
  }

  async getAuswertungBisProjekteAlleDaten(
    meldeZeitraumVon: Date,
    meldeZeitraumBis: Date,
  ): Promise<ProjektItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/auswertungbisprojektealledaten${toQueryString({ meldeZeitraumVon, meldeZeitraumBis })}`,
      { method: "GET" },
    );
  }

  async getProjektpartnerListeAlleAktivenBIS(
    projektIDs: number[],
  ): Promise<ProjektPartnerZuweisungItem[]> {
    return this.request(
      `${REPORTS_BASE_URL}/projektpartnerlistealleaktivenbis`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(projektIDs),
      },
    );
  }

  async getProjektListeAlleFelder(): Promise<ProjektItem[]> {
    return this.request(`${REPORTS_BASE_URL}/projektlisteallefelder`, {
      method: "GET",
    });
  }
}
